import React from 'react';
import { Users, Briefcase } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { ThemedInput } from '../../themed';

/**
 * PassengerDetailsSection Component
 *
 * Displays rider count and luggage inputs with dynamic warning messages.
 * Luggage field only shows if maximumLuggagePerRider > 0.
 * Warning color changes based on enforcement (red for hard limit, yellow for soft limit).
 *
 * State Ownership:
 * - Reads all props from parent (no local state)
 * - Rider/luggage values managed by parent component
 *
 * @param {Object} props
 * @param {string} props.riders - Number of riders
 * @param {string} props.luggage - Number of luggage pieces
 * @param {Function} props.onChange - Handler for input changes
 * @param {number} props.maximumLuggagePerRider - Max luggage per rider (0 = hide field)
 * @param {boolean} props.maximumLuggageEnforced - Whether limit is enforced (hard limit)
 * @param {string} props.luggageWarningMessage - Custom translated warning message
 */
const PassengerDetailsSection = ({
  riders,
  luggage,
  onChange,
  maximumLuggagePerRider,
  maximumLuggageEnforced,
  luggageWarningMessage
}) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();

  const parsedRiders = parseInt(riders) || 1;
  const totalAllowed = parsedRiders * maximumLuggagePerRider;

  return (
    <>
      {/* Riders Input */}
      <div>
        <label className={cn("block text-sm font-medium mb-1 items-center", text.primary)}>
          <Users className="w-5 h-4 inline-block mr-1 text-blue-500" />
          {t('numberOfRiders')}
        </label>
        <ThemedInput
          type="number"
          name="riders"
          value={riders}
          onChange={onChange}
          min="1"
          className="w-full"
          placeholder={t('Enter number of riders')}
        />
      </div>

      {/* Luggage Input - Only show if maximumLuggagePerRider > 0 */}
      {maximumLuggagePerRider > 0 && (
        <div>
          <label className={cn("block text-sm font-medium mb-1 items-center", text.primary)}>
            <Briefcase className="w-5 h-4 inline-block mr-1 text-blue-500" />
            {t('piecesOfLuggage')}
          </label>
          <ThemedInput
            type="number"
            name="luggage"
            value={luggage}
            onChange={onChange}
            min="0"
            className="w-full"
            placeholder={t('Enter number of luggage items')}
          />

          {/* Warning Message - Color based on enforcement */}
          <div className={cn(
            "mt-1 text-sm",
            maximumLuggageEnforced ? "text-red-600" : "text-yellow-600"
          )}>
            <p>
              {luggageWarningMessage ? (
                // Show custom translated message from database
                luggageWarningMessage
              ) : (
                // Show default message with dynamic calculation
                t('maximumLuggagePerRiderWarning', {
                  maxPerRider: maximumLuggagePerRider,
                  riders: parsedRiders,
                  totalAllowed: totalAllowed
                })
              )}
            </p>
          </div>
        </div>
      )}
    </>
  );
};


export default React.memo(PassengerDetailsSection);
